/**
 * Breakdown ("Itemization") rows — telling an ADDITIONAL CHARGE (tip / discount / tax / other)
 * from a LINE ITEM on the way back from the API.
 *
 * The wire carries every breakdown row as `type: "Item"` (the backend rejects anything else) with
 * only a `sing` sign, so a saved charge can't be recognised by `type`. The charge picker only ever
 * offers the names below, so a row named one of them — or carrying a "-" sign, which only a
 * discount does — is a charge. Without this, an edited expense listed its Tip/Discount/Tax/Other
 * as line items and left "Additional charges" empty.
 */
export const BREAKDOWN_CHARGE_NAMES = ['Tip', 'Discount', 'Tax', 'Other'] as const;
export type BreakdownChargeName = (typeof BREAKDOWN_CHARGE_NAMES)[number];

/** Case-insensitive, whitespace-tolerant match against the charge picker's names. */
export function isBreakdownChargeName(name: string): boolean {
  const needle = name.trim().toLowerCase();
  return BREAKDOWN_CHARGE_NAMES.some((charge) => charge.toLowerCase() === needle);
}

/**
 * How a charge's sign is decided: a Discount ALWAYS subtracts (`minus`), a Tip or Tax always adds
 * (`plus`), and "Other" (or any unrecognised name) is `free` — the user picks + or − per row via
 * the operator toggle, and that choice travels with the row (`sign`) through totals, the wire and
 * the edit prefill.
 */
export type ChargeSignMode = 'plus' | 'minus' | 'free';

export function chargeSignMode(name: string): ChargeSignMode {
  const key = name.trim().toLowerCase();
  if (key === 'discount') return 'minus';
  if (key === 'tip' || key === 'tax') return 'plus';
  return 'free';
}

/** A saved row's kind: an explicit `charge` type, a "-" sign, or a charge name ⇒ `charge`. */
export function classifyBreakdownRow(row: {
  readonly type?: string | null | undefined;
  readonly name?: string | null | undefined;
  readonly sign?: string | null | undefined;
}): 'item' | 'charge' {
  if ((row.type ?? '').toLowerCase() === 'charge') return 'charge';
  if (row.sign === '-') return 'charge';
  return isBreakdownChargeName(row.name ?? '') ? 'charge' : 'item';
}
